import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import {
  buscaNotasXMLConvertido,
  enviaNotasXMLConvertido,
} from '@/services/SimplesNacional/Segregacao/api/SegregacaoApi';
import type { XmlConvertidoPayload } from '@/services/SimplesNacional/Segregacao/api/types';
import { fmtMoeda } from '@/lib/utils/formatters';

const STATUS_COLOR: Record<string, string> = {
  '1': 'bg-yellow-500',
  '2': 'bg-orange-500',
  '3': 'bg-green-500',
  '9': 'bg-gray-500',
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payload: XmlConvertidoPayload | null;
}

export function XMLsConvertidosDialog({ open, onOpenChange, payload }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['xmls-convertidos', payload?.CnpjContribuinte, payload?.Ano, payload?.Mes],
    queryFn: () => buscaNotasXMLConvertido(payload!),
    enabled: open && !!payload,
    staleTime: 30_000,
  });

  const { mutate: reenviar, isPending } = useMutation({
    mutationFn: () => enviaNotasXMLConvertido(payload!),
    onSuccess: () => {
      toast({ title: 'XMLs enviados para conversão com sucesso.' });
      queryClient.invalidateQueries({
        queryKey: ['xmls-convertidos', payload?.CnpjContribuinte, payload?.Ano, payload?.Mes],
      });
    },
    onError: () => {
      toast({ title: 'Erro ao enviar XMLs', variant: 'destructive' });
    },
  });

  const itens = data?.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>XMLs Convertidos</DialogTitle>
          <DialogDescription>
            Notas fiscais enviadas para conversão de XML no período selecionado.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end mb-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            onClick={() => reenviar()}
            disabled={isPending || !payload}
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Reenviar XMLs
          </Button>
        </div>

        <div className="overflow-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Carregando...</span>
            </div>
          ) : itens.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">
              Nenhuma nota encontrada para o período.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Série</th>
                  <th className="px-3 py-2 text-left font-medium">Número</th>
                  <th className="px-3 py-2 text-left font-medium">Data Emissão</th>
                  <th className="px-3 py-2 text-right font-medium">Valor</th>
                  <th className="px-3 py-2 text-left font-medium">Chave Acesso</th>
                </tr>
              </thead>
              <tbody>
                {itens.map(item => (
                  <tr key={item.ID} className="border-b hover:bg-muted/20">
                    <td className="px-3 py-2">
                      <Badge
                        className={`${STATUS_COLOR[item.Status] ?? 'bg-gray-400'} text-white text-xs`}
                      >
                        {item.StatusDescricao}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">{item.Serie}</td>
                    <td className="px-3 py-2">{item.Numero}</td>
                    <td className="px-3 py-2">{item.DataEmissao}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoeda(item.Valor)}</td>
                    <td
                      className="px-3 py-2 font-mono text-xs truncate max-w-[200px]"
                      title={item.ChaveAcessoDOC}
                    >
                      {item.ChaveAcessoDOC}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
